import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@services/firebase';
import { Eye, EyeOff, Trash2, RefreshCw, Plus, Pencil, Star } from 'lucide-react';
import toast from 'react-hot-toast';

export default function AdminPricing() {
  const navigate = useNavigate();
  const [plans, setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'pricing'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      setPlans(list);
    } catch {
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const toggleVisible = async (id, current) => {
    try {
      await updateDoc(doc(db, 'pricing', id), { visible: !current });
      setPlans(prev => prev.map(p => p.id === id ? { ...p, visible: !current } : p));
      toast.success(current ? 'Plan hidden' : 'Plan visible');
    } catch {
      toast.error('Failed to update plan');
    }
  };

  const deletePlan = async (id, name) => {
    if (!window.confirm(`Delete "${name}" plan?`)) return;
    try {
      await deleteDoc(doc(db, 'pricing', id));
      setPlans(prev => prev.filter(p => p.id !== id));
      toast.success('Plan deleted');
    } catch {
      toast.error('Failed to delete plan');
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pricing Plans</h1>
          <p className="text-gray-500 text-sm mt-1">{plans.length} total plans</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPlans}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
          <button
            onClick={() => navigate('/admin/pricing/new')}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={15} />
            New Plan
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">Loading…</div>
        ) : plans.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-2">
            <p>No pricing plans yet.</p>
            <button
              onClick={() => navigate('/admin/pricing/new')}
              className="mt-2 flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={14} /> Create your first plan
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Plan</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Price</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Features</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Popular</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Order</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Visible</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {plans.map(plan => {
                const isVisible = plan.visible !== false;
                return (
                  <tr key={plan.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-800">{plan.name || 'Untitled'}</div>
                      <div className="text-xs text-gray-400 mt-0.5 max-w-xs truncate">{plan.subtitle || ''}</div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-gray-700">
                      {plan.price || '—'}
                      {plan.period && <span className="text-xs text-gray-400 font-normal">{plan.period}</span>}
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {(plan.features || []).length} items
                    </td>
                    <td className="px-6 py-4">
                      {plan.popular && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">
                          <Star size={11} fill="currentColor" /> Popular
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-500">{plan.order ?? '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${isVisible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {isVisible ? 'Visible' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => navigate(`/admin/pricing/${plan.id}/edit`)}
                          title="Edit"
                          className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => toggleVisible(plan.id, isVisible)}
                          title={isVisible ? 'Hide' : 'Show'}
                          className={`p-1.5 rounded-lg transition-colors ${isVisible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                        >
                          {isVisible ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                        <button
                          onClick={() => deletePlan(plan.id, plan.name)}
                          title="Delete"
                          className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
